import moment from 'moment';

/**
 * Dates, guests, and lodging stored on an inquiry transaction.
 *
 * The transaction is loaded from the API. This reads protectedData written
 * when the inquiry was created. It does not read a package id, a booking
 * seat count, or a price. The order panel prices the trip the same way it
 * does for an empty selection.
 */

const calendarDateFromParts = parts => {
  const year = Number(parts?.year);
  const month = Number(parts?.month);
  const day = Number(parts?.day);

  if (!year || !month || !day) {
    return null;
  }

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const calendarDateFromIso = value => {
  if (!value) {
    return null;
  }

  const parsed = moment(value);
  if (!parsed.isValid()) {
    return null;
  }

  return new Date(parsed.year(), parsed.month(), parsed.date());
};

const readPartySize = partySize => {
  if (partySize && typeof partySize === 'object') {
    const count = parseInt(partySize.partySize, 10);
    if (!Number.isFinite(count) || count <= 0) {
      return null;
    }

    const next = { partySize: count };
    if (partySize.flexibleGuestSize) {
      next.flexibleGuestSize = partySize.flexibleGuestSize;
    }
    return next;
  }

  const count = parseInt(partySize, 10);
  return Number.isFinite(count) && count > 0 ? count : null;
};

const readLodgingAddons = lodgingAddons => {
  if (!Array.isArray(lodgingAddons)) {
    return [];
  }

  return lodgingAddons.filter(index => Number.isInteger(index) && index >= 0);
};

export const readStoredInquirySelection = transaction => {
  const protectedData = transaction?.attributes?.protectedData;
  if (!protectedData) {
    return null;
  }

  const startDate =
    calendarDateFromParts(protectedData.emailStart) ||
    calendarDateFromIso(protectedData.startDate);
  const endDate =
    calendarDateFromParts(protectedData.emailEnd) ||
    calendarDateFromIso(protectedData.endDate) ||
    startDate;
  const startLodgingDate = calendarDateFromParts(protectedData.emailStartLodgingDate);
  let dates = null;

  if (startDate && endDate && endDate.getTime() >= startDate.getTime()) {
    dates = {
      startDate,
      endDate,
      ...(startLodgingDate ? { startLodgingDate } : {}),
    };
  }

  const partySize =
    readPartySize(protectedData.partySize) ??
    readPartySize(protectedData.selectedPackage?.packageParty);
  const lodgingAddons = readLodgingAddons(protectedData.lodgingAddons);

  if (!dates && partySize == null && lodgingAddons.length === 0) {
    return null;
  }

  return {
    dates,
    partySize: partySize == null ? undefined : partySize,
    lodgingAddons,
  };
};

/**
 * Listing order-panel values to carry into the inquiry form.
 * Price and package id on the selection are not copied. The inquiry keeps
 * the package already chosen on the package page.
 */
export const listingSelectionToInquiryState = selection => {
  if (!selection) {
    return {};
  }

  const dates = selection.bookingDates;
  const next = {};

  if (dates?.startDate && dates?.endDate) {
    next.bookingDates = {
      startDate: dates.startDate,
      endDate: dates.endDate,
      ...(dates.startLodgingDate ? { startLodgingDate: dates.startLodgingDate } : {}),
    };
    next.dates = next.bookingDates;
  }

  const partySize = readPartySize(selection.partySize);
  if (partySize != null) {
    next.partySize = partySize;
  }

  const lodgingAddons = readLodgingAddons(selection.lodgingAddons);
  if (lodgingAddons.length) {
    next.lodgingAddons = lodgingAddons;
  }

  return next;
};
